import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';

import { useTerritoryStore } from '@/stores/territoryStore';
import { useTerritory } from '@/hooks/useTerritory';
import { geometryBounds } from '@/utils/geo';

/**
 * useMapRecenter — recadre une carte MapLibre sur l'emprise réelle du territoire actif.
 *
 * Dès que la carte est chargée (`mapReady`) et que la géométrie du territoire actif
 * est disponible, on appelle `fitBounds` sur sa bbox. Les cartes conservent ainsi un
 * `initialViewState` fixe pour le premier rendu ; le recentrage prend le relais sans
 * coordonnées en dur.
 *
 * NB : un « écran de carte blanc au chargement » n'apparaît QUE lorsque l'onglet est
 * en arrière-plan (document.hidden) — requestAnimationFrame y est gelé, donc la boucle
 * de rendu MapLibre ne démarre pas. En usage réel (onglet visible) les cartes
 * s'affichent normalement ; aucun contournement n'est nécessaire ici.
 *
 * @param mapRef   Ref vers l'instance MapLibre (`<Map ref={mapRef}>`).
 * @param mapReady Passe à `true` dans `onLoad` du `<Map>`.
 */
export function useMapRecenter(mapRef: RefObject<MapRef | null>, mapReady: boolean): void {
  const activeTerritory = useTerritoryStore((s) => s.activeTerritory);
  const { data: territory } = useTerritory(activeTerritory?.code_insee ?? null);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    const bounds = geometryBounds(territory?.geometry);
    if (!map || !bounds) return;
    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      { padding: 48, maxZoom: 16, duration: 1200 },
    );
  }, [mapRef, mapReady, territory?.geometry]);
}
